import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageBanner from '../../components/PageBanner';
import { getIqac } from '../../services/data.service';
import { mediaUrl } from '../../services/assetUrl';

// Subtle, consistent scroll-reveal used across the page
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};
const reveal = {
  variants: fadeUp,
  initial: 'hidden',
  whileInView: 'show',
  viewport: { once: true, amount: 0.12 }
};

// Animated count-up for the credibility stats band
const CountUp = ({ to, suffix = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.round(v));
  const [val, setVal] = useState(0);

  useEffect(() => rounded.on('change', (v) => setVal(v)), [rounded]);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, { duration: 1.4, ease: 'easeOut' });
    return controls.stop;
  }, [inView, to, mv]);

  return <span ref={ref}>{val}{suffix && <span>{suffix}</span>}</span>;
};

const PdfDocIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const IqacDocRow = ({ doc }) => (
  <a
    href={mediaUrl(doc.file || doc.link)}
    target="_blank"
    rel="noreferrer"
    className="iqac-file-item"
  >
    <span className="iqac-file-item__icon"><PdfDocIcon /></span>
    <span className="iqac-file-item__title">{doc.title}</span>
    <span className="iqac-file-item__arrow" aria-hidden="true">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
      </svg>
    </span>
  </a>
);

const NaacReportsLibrary = ({ groups }) => {
  const groupList = Object.values(groups);
  const [activeGroup, setActiveGroup] = useState(groupList[0]?.id);
  const [visibleCount, setVisibleCount] = useState(4);
  const current = groups[activeGroup] || groupList[0];

  useEffect(() => {
    setVisibleCount(4);
  }, [activeGroup]);

  if (!current) return null;
  const visibleItems = current.items.slice(0, visibleCount);

  return (
    <div className="iqac-doc-library">
      <div className="iqac-doc-library__tabs" role="tablist" aria-label="NAAC report categories">
        {groupList.map((group) => (
          <button
            key={group.id}
            type="button"
            role="tab"
            aria-selected={activeGroup === group.id}
            className={`iqac-doc-library__tab${activeGroup === group.id ? ' is-active' : ''}`}
            onClick={() => setActiveGroup(group.id)}
          >
            {group.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          className="iqac-doc-library__panel"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <header className="iqac-doc-library__head">
            <p>{current.description}</p>
          </header>

          <div className="iqac-file-list">
            {visibleItems.map((doc, idx) => (
              <IqacDocRow key={`${current.id}-${idx}`} doc={doc} />
            ))}
          </div>

          {(visibleCount < current.items.length || visibleCount > 4) && (
            <div className="iqac-doc-library__more">
              {visibleCount < current.items.length && (
                <button type="button" onClick={() => setVisibleCount((c) => c + 4)}>
                  Show more documents
                </button>
              )}
              {visibleCount > 4 && (
                <button type="button" className="is-muted" onClick={() => setVisibleCount(4)}>
                  Show less
                </button>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const PolicyDocumentsLibrary = ({ items }) => {
  const [visibleCount, setVisibleCount] = useState(6);
  const visibleItems = items.slice(0, visibleCount);

  return (
    <div className="iqac-doc-library iqac-doc-library--policies">
      <header className="iqac-doc-library__head iqac-doc-library__head--solo">
        <p>Institutional policies on quality assurance, student welfare, research, and governance.</p>
      </header>

      <div className="iqac-file-list">
        {visibleItems.map((doc, idx) => (
          <IqacDocRow key={idx} doc={doc} />
        ))}
      </div>

      {(visibleCount < items.length || visibleCount > 6) && (
        <div className="iqac-doc-library__more">
          {visibleCount < items.length && (
            <button type="button" onClick={() => setVisibleCount((c) => c + 3)}>
              Show more policies
            </button>
          )}
          {visibleCount > 6 && (
            <button type="button" className="is-muted" onClick={() => setVisibleCount(6)}>
              Show less
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const PremiumDocCard = ({ doc, iconSvg }) => {
  return (
    <a href={mediaUrl(doc.file || doc.link)} target="_blank" rel="noreferrer" className="premium-doc-card">
      <div className="doc-icon-top">
        {iconSvg || (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        )}
      </div>
      <div className="doc-title-main">{doc.title}</div>
      <div className="doc-subtitle">PDF Document</div>
      <div className="doc-action-bottom">
        <span>View Document</span>
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
      </div>
    </a>
  );
};

const AccreditationHub = ({ hub }) => {
  const hubList = Object.values(hub);
  const [activeHub, setActiveHub] = useState(hubList[0]?.id);
  const current = hub[activeHub] || hubList[0];

  if (!current) return null;

  return (
    <div className="iqac-acc-hub">
      <div className="iqac-acc-hub__shell">
        <aside className="iqac-acc-hub__rail" aria-label="Accreditation categories">
          <p className="iqac-acc-hub__rail-label">Select category</p>
          {hubList.map((h) => (
            <button
              key={h.id}
              type="button"
              className={`iqac-acc-hub__tab${activeHub === h.id ? ' is-active' : ''}`}
              onClick={() => setActiveHub(h.id)}
              aria-pressed={activeHub === h.id}
            >
              <span className="iqac-acc-hub__tab-eyebrow">{h.eyebrow}</span>
              <span className="iqac-acc-hub__tab-title">{h.tabLabel}</span>
              <span className="iqac-acc-hub__tab-stat">{h.stat}</span>
            </button>
          ))}
        </aside>

        <div className="iqac-acc-hub__stage">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              className="iqac-acc-hub__panel"
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -18 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <header className="iqac-acc-hub__panel-head">
                <span className="iqac-acc-hub__panel-eyebrow">{current.eyebrow}</span>
                <h3>{current.title}</h3>
                <p>{current.description}</p>
              </header>

              <div className="iqac-acc-hub__destinations">
                {(current.links || []).map((link, index) => (
                  <Link key={link.to || index} to={link.to} className="iqac-acc-hub__dest">
                    <span className="iqac-acc-hub__dest-num">{String(index + 1).padStart(2, '0')}</span>
                    <span className="iqac-acc-hub__dest-body">
                      <strong>{link.label}</strong>
                      <small>{link.meta}</small>
                    </span>
                    <span className="iqac-acc-hub__dest-cta">
                      Open
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </span>
                  </Link>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const ExpandableGrid = ({ items, initialCount = 3, renderItem, gridClass = "hub-grid", gridStyle = {} }) => {
  const [visibleCount, setVisibleCount] = useState(initialCount);

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + initialCount);
  };

  const handleLoadLess = () => {
    setVisibleCount(initialCount);
  };

  return (
    <div style={{ marginBottom: '3rem' }}>
      <div className={gridClass} style={gridStyle}>
        {items.slice(0, visibleCount).map(renderItem)}
      </div>
      {(visibleCount < items.length || visibleCount > initialCount) && (
        <div className="load-more-container" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '2rem' }}>
          {visibleCount < items.length && (
            <button className="load-more-btn" onClick={handleLoadMore}>
              Load More
            </button>
          )}
          {visibleCount > initialCount && (
            <button className="load-more-btn" onClick={handleLoadLess}>
              Load Less
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const SectionHeading = ({ children, fontSize = '1.4rem' }) => (
  <h3 style={{ marginBottom: '1.5rem', fontSize, color: '#0e2455', fontWeight: 800 }}>{children}</h3>
);

const Iqac = () => {
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [activeTab, setActiveTab] = useState('core');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getIqac();
        if (active) setModel(res?.data || null);
      } catch (e) {
        if (active) setModel(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="iqac-page-root">
        <Header />
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: '#53545b', fontSize: '1.1rem' }}>Loading…</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!model) {
    return (
      <div className="iqac-page-root">
        <Header />
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px' }}>
          <h2 style={{ color: '#0e2455' }}>IQAC content unavailable</h2>
          <p style={{ color: '#53545b' }}>Please try again later.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const hero = model.hero || {};
  const about = model.about || {};
  const stats = model.stats || [];
  const members = model.members || {};
  const peer = model.peerTeam || {};
  const docHub = model.documentHub || {};
  const policies = model.policies || [];

  const memberItems = members.items || [];
  const displayedMembers = showAllMembers ? memberItems : memberItems.slice(0, 8);

  // Build keyed maps for the hub + report library components.
  const accHub = (model.accreditationHub || []).reduce((acc, h) => {
    if (h.id) acc[h.id] = h;
    return acc;
  }, {});
  const naacGroups = {
    selfStudy: {
      id: 'selfStudy',
      label: 'Self Study Reports',
      description: 'Self-study reports, institutional information, and DVV clarifications submitted for NAAC assessment.',
      items: model.naacReports?.selfStudy || [],
    },
    peerTeam: {
      id: 'peerTeam',
      label: 'Peer Team & Outcomes',
      description: 'Peer team evaluation reports, grade sheets, and official NAAC accreditation certificates.',
      items: model.naacReports?.peerTeam || [],
    },
  };

  return (
    <div className="iqac-page-root">
      <Header />

      {/* Hero / Banner */}
      <PageBanner
        title={hero.title}
        eyebrow={hero.eyebrow}
        subtitle={hero.subtitle}
        image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
      >
        {hero.circularLink && (
          <a href={mediaUrl(hero.circularLink)} target="_blank" rel="noreferrer" className="circular-btn" style={{ marginTop: '20px', display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.1)', padding: '10px 20px', borderRadius: '50px', color: '#fff', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.2)' }}>
            <span>{hero.circularLabel || 'View IQAC Circular'}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        )}
      </PageBanner>

      {/* Credibility stats band */}
      {stats.length > 0 && (
        <motion.section className="iqac-stats" {...reveal}>
          <div className="iqac-stats__inner">
            {stats.map((s, i) => (
              <div key={i} className="iqac-stats__item">
                <div className="iqac-stats__value">
                  {s.plain ? s.value : <CountUp to={s.value} suffix={s.suffix} />}
                </div>
                <div className="iqac-stats__label">{s.label}</div>
              </div>
            ))}
          </div>
        </motion.section>
      )}

      {/* About Section */}
      <motion.section className="iqac-about-v2" {...reveal}>
        <div className="iqac-about-v2__inner">
          <div className="iqac-about-v2__intro">
            <span className="iqac-eyebrow">{about.eyebrow}</span>
            <h2>{about.heading}</h2>
            <p>{about.description}</p>
            {about.establishedDate && (
              <div className="iqac-established">
                <span>{about.establishedLabel || 'Established'}</span>
                <strong>{about.establishedDate}</strong>
              </div>
            )}
          </div>

          <div className="iqac-about-v2__gallery">
            {(about.images || []).map((img, i) => (
              <div className="iqac-gallery-card" key={i}>
                <img src={mediaUrl(img.src)} alt={img.alt || `IQAC ${i + 1}`} loading="lazy" />
              </div>
            ))}
          </div>
        </div>

        {(about.pillars || []).length > 0 && (
          <div className="iqac-pillars">
            {about.pillars.map((p, i) => {
              const mod = (p.tag || '').trim().toLowerCase();
              return (
                <article className={`iqac-pillar iqac-pillar--${mod}`} key={i}>
                  <span className="iqac-pillar__tag">{p.tag}</span>
                  <h3>{p.title}</h3>
                  {(p.points && p.points.length > 0) ? (
                    <ul>
                      {p.points.map((pt, idx) => <li key={idx}>{pt}</li>)}
                    </ul>
                  ) : (
                    <p>{p.description}</p>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </motion.section>

      {/* Members Section */}
      <motion.section className="iqac-members-v2" {...reveal}>
        <div className="iqac-members-v2__inner">
          <div className="iqac-section-head">
            <span className="iqac-eyebrow">{members.eyebrow}</span>
            <h2>{members.heading}</h2>
            <p>{members.description}</p>
          </div>

          <div className="iqac-members-grid">
            {displayedMembers.map((member) => (
              <article className="iqac-member-card" key={member.sn}>
                <div className="iqac-member-card__index">{String(member.sn).padStart(2, '0')}</div>
                <div className="iqac-member-card__body">
                  <h3>{member.name}</h3>
                  <span>{member.designation}</span>
                </div>
              </article>
            ))}
          </div>

          {memberItems.length > 8 && (
            <div className="iqac-members-toggle">
              <button type="button" onClick={() => setShowAllMembers(!showAllMembers)}>
                {showAllMembers ? 'Show Less Members' : 'View All Members'}
              </button>
            </div>
          )}
        </div>
      </motion.section>

      {/* Peer Team Section */}
      <motion.section className="iqac-peer-v2" {...reveal}>
        <div className="iqac-peer-v2__inner">
          <div className="iqac-section-head">
            <span className="iqac-eyebrow">{peer.eyebrow}</span>
            <h2>{peer.heading}</h2>
            <p>{peer.description}</p>
          </div>

          <div className="iqac-peer-layout">
            {peer.videoUrl && (
              <aside className="iqac-peer-media">
                <a
                  href={peer.videoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="iqac-peer-video"
                >
                  <img src={mediaUrl(peer.videoImage)} alt="NAAC Peer Team Visit" loading="lazy" />
                  <span className="iqac-peer-video__overlay">
                    <span className="iqac-peer-video__play">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span className="iqac-peer-video__label">Watch Peer Team Visit Highlights</span>
                  </span>
                </a>
              </aside>
            )}

            <div className="iqac-peer-list">
              {(peer.items || []).map((p) => (
                <article className="iqac-peer-card" key={p.sn}>
                  <div className="iqac-peer-card__index">{String(p.sn).padStart(2, '0')}</div>
                  <div className="iqac-peer-card__content">
                    <h3>{p.name}</h3>
                    <p className="role">{p.designation}</p>
                    <p className="institute">{p.institute}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Interactive Document Hub */}
      <motion.section className="document-hub-section" {...reveal}>
        <div className="section-header">
          <span className="section-badge">RESOURCES</span>
          <h2 className="section-title">{docHub.heading || 'IQAC Document Hub'}</h2>
        </div>

        <div className="hub-tabs">
          <button className={`hub-tab-btn ${activeTab === 'core' ? 'active' : ''}`} onClick={() => setActiveTab('core')}>
            Core Documents
          </button>
          <button className={`hub-tab-btn ${activeTab === 'academics' ? 'active' : ''}`} onClick={() => setActiveTab('academics')}>
            Academics
          </button>
          <button className={`hub-tab-btn ${activeTab === 'quality' ? 'active' : ''}`} onClick={() => setActiveTab('quality')}>
            Quality & Feedback
          </button>
        </div>

        <motion.div
          className="hub-content"
          key={activeTab}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {activeTab === 'core' && (
            <>
              <SectionHeading>Minutes of Meeting</SectionHeading>
              <ExpandableGrid items={docHub.minutes || []} renderItem={(doc, idx) => <PremiumDocCard key={idx} doc={doc} />} />

              <SectionHeading>IQAC Reports</SectionHeading>
              <ExpandableGrid items={docHub.iqacReports || []} renderItem={(doc, idx) => <PremiumDocCard key={idx} doc={doc} />} />

              <SectionHeading>Organogram</SectionHeading>
              <ExpandableGrid items={docHub.organogram || []} renderItem={(doc, idx) => <PremiumDocCard key={idx} doc={doc} />} />
            </>
          )}

          {activeTab === 'academics' && (
            <>
              <SectionHeading>Academic Calendars</SectionHeading>
              <ExpandableGrid items={docHub.academicCalendars || []} renderItem={(doc, idx) => <PremiumDocCard key={idx} doc={doc} />} />

              <SectionHeading>Scheme and Syllabus</SectionHeading>
              <ExpandableGrid items={docHub.schemeAndSyllabus || []} renderItem={(doc, idx) => <PremiumDocCard key={idx} doc={doc} />} />
            </>
          )}

          {activeTab === 'quality' && (
            <>
              <SectionHeading>Student Satisfaction Surveys</SectionHeading>
              <ExpandableGrid items={docHub.surveys || []} renderItem={(doc, idx) => <PremiumDocCard key={idx} doc={doc} />} />
            </>
          )}
        </motion.div>
      </motion.section>

      {/* Accreditation Hub — NAAC & AQAR */}
      {Object.keys(accHub).length > 0 && (
        <motion.section className="iqac-accreditation-wrap" {...reveal}>
          <div className="section-header">
            <span className="section-badge">ACCREDITATION</span>
            <h2 className="section-title">Accreditation &amp; Compliance</h2>
          </div>
          <AccreditationHub hub={accHub} />
        </motion.section>
      )}

      {/* Self Study & Peer Team Reports */}
      <motion.section className="iqac-reports-wrap" {...reveal}>
        <div className="section-header">
          <span className="section-badge">NAAC REPORTS</span>
          <h2 className="section-title">Self Study &amp; Peer Team Reports</h2>
        </div>
        <NaacReportsLibrary groups={naacGroups} />
      </motion.section>

      {/* Policy Documents */}
      {policies.length > 0 && (
        <motion.section className="iqac-policies-wrap" {...reveal}>
          <div className="section-header">
            <span className="section-badge">POLICIES</span>
            <h2 className="section-title">Policy Documents</h2>
          </div>
          <PolicyDocumentsLibrary items={policies} />
        </motion.section>
      )}

      <Footer />
    </div>
  );
};

export default Iqac;
